import {
  Document,
  Font,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useMemo } from "react";

import { TableProps } from "./types";
const logoUrl = new URL("../../../../../public/logo.jpg", import.meta.url).href;
const interUrl = new URL("../../../../../public/Inter.ttf", import.meta.url).href;
const interSemiUrl = new URL("../../../../../public/InterSemi.ttf", import.meta.url).href;

const Pdf = ({ letter }) => {
  const styles = StyleSheet.create({
    paragraph: {
      textIndent: 20,
      textAlign: "justify",
      lineHeight: 1.5,
      fontSize: 12,
      fontFamily: "Inter",
    },
    title: {
      lineHeight: 1.5,
      fontFamily: "Inter",
      fontWeight: "semibold",
      fontSize: 12,
    },
    row: {
      borderBottom: 1,
      borderColor: "#000",
      flexDirection: "row",
    },
    col: {
      borderLeft: 1,
      borderColor: "#000",
      width: "15%",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 5,
    },
    tableText: {
      textAlign: "center",
      width: "100%",
      fontSize: 12,
      fontFamily: "Inter",
    },
    line: {
      height: 1,
      backgroundColor: "#000",
    },
  });

  Font.register({
    family: "Inter",
    fonts: [
      {
        src: interUrl,
      },
      {
        fontWeight: "600",
        src: interSemiUrl,
      },
    ],
  } as any);

  const data = useMemo(() => {
    const res: TableProps[] = [];
    if (letter.letter_type === "percentage") {
      letter.consumers_with_facilities?.forEach((consumer) => {
        consumer.facilities.forEach((facility) => {
          if (facility.facility_percentage === 0) {
            return;
          }
          res.push({
            value: facility.facility_percentage,
            consumer_name: consumer.consumer_name,
            facility_code: facility.facility_code,
            client_code: facility.facility_client_code,
            address: facility.facility_full_address,
          });
        });
      });

      return res;
    }

    letter.current_order.forEach((order, index) => {
      res.push({
        value: index + 1,
        address: order.address,
        consumer_name: order.consumer_name,
        client_code: order.client_code,
        facility_code: order.facility_code,
      });
    });

    return res;
  }, [letter.consumers_with_facilities, letter.current_order, letter.letter_type]);

  const leftoverCode = useMemo(() => {
    if (letter.letter_type === "preference") {
      return letter.current_order.find((order) => order.id === letter.leftover)?.facility_code;
    }
    return "";
  }, [letter.current_order, letter.letter_type, letter.leftover]);

  return (
    <PDFViewer className="h-screen w-screen">
      <Document>
        <Page size="A4" style={{ padding: 20 }}>
          <View
            style={{
              flexDirection: "row",
              alignContent: "center",
              justifyContent: "center",
            }}
          >
            <Image src={logoUrl} style={{ width: 100, height: 40 }} />
          </View>

          <View style={{ flexDirection: "column", paddingHorizontal: 30, gap: 14 }}>
            <Text
              style={{
                color: "#ff8b0e",
                fontSize: 14,
                width: "100%",
                textAlign: "center",
                fontWeight: "semibold",
                fontFamily: "Inter",
              }}
            >
              FORMULÁRIO DE RATEIO DOS CRÉDITOS POR{" "}
              {letter.letter_type === "percentage" ? "PORCENTAGEM" : "PRIORIDADE"}
            </Text>

            <Text style={styles.title}>Objetivo</Text>

            <Text style={styles.paragraph}>
              Declarar o formato de rateio da energia excedente que será destinada a unidade
              consumidora <Text style={{ textDecoration: "underline" }}>principal (geradora)</Text>{" "}
              e a cada unidade <Text style={{ textDecoration: "underline" }}>consumidora</Text>{" "}
              (consumidor) de energia elétrica.
            </Text>

            <Text style={styles.title}>
              {letter.letter_type === "percentage"
                ? "Rateio por percentuais"
                : "Rateio por ordem de prioridade"}
            </Text>

            <Text style={styles.paragraph}>
              {letter.letter_type === "percentage"
                ? "Preencha a tabela indicando os percentuais de participação no rateio de excedentes, inserindo ou removendo linhas da tabela, conforme a necessidade."
                : "Preencha a tabela com a ordem de prioridade das unidades consumidoras que utilizarão a energia excedente, inserindo ou removendo linhas da tabela, conforme a necessidade."}
            </Text>

            <View
              style={{
                border: 1,
                borderColor: "#000",
                width: "100%",
                borderLeft: 0,
                borderBottom: 0,
              }}
            >
              <View style={[styles.row, { backgroundColor: "#ff910f", color: "#fff" }]}>
                <View style={[styles.col, { width: "20%", fontWeight: "semibold" }]}>
                  <Text style={styles.tableText}>Código do Cliente</Text>
                </View>
                <View style={[styles.col, { width: "40%", fontWeight: "semibold" }]}>
                  <Text style={styles.tableText}>Endereço</Text>
                </View>
                <View style={[styles.col, { width: "25%", fontWeight: "semibold" }]}>
                  <Text style={styles.tableText}>Unidade{"\n"}Consumidora</Text>
                </View>
                <View style={[styles.col, { width: "15%", fontWeight: "semibold" }]}>
                  <Text style={styles.tableText}>
                    {letter.letter_type === "percentage" ? "Percentual" : "Prioridade"}
                  </Text>
                </View>
              </View>
              {data.map((item, index) => (
                <View style={styles.row} key={index}>
                  <View style={[styles.col, { width: "20%" }]}>
                    <Text style={styles.tableText}>{item.client_code}</Text>
                  </View>
                  <View style={[styles.col, { width: "40%" }]}>
                    <Text style={styles.tableText}>{item.address}</Text>
                  </View>
                  <View style={[styles.col, { width: "25%" }]}>
                    <Text style={styles.tableText}>{item.facility_code}</Text>
                  </View>
                  <View style={[styles.col, { width: "15%" }]}>
                    <Text style={styles.tableText}>
                      {item.value}
                      {letter.letter_type === "percentage" && "%"}
                    </Text>
                  </View>
                </View>
              ))}
            </View>

            {letter.letter_type === "preference" && (
              <>
                <Text style={styles.paragraph}>
                  Após a distribuição da energia excedente, conforme solicitação acima, e havendo um
                  eventual montante remanescente de energia, caso deseje indicar uma unidade
                  consumidora para destinar como crédito de energia, favor informar o número da
                  unidade consumidora (instalação do cliente) abaixo:
                </Text>
                <Text style={styles.paragraph}>Número da Instalação: {leftoverCode}</Text>
                <Text style={styles.paragraph}>
                  Caso não seja indicada a unidade consumidora, o montante remanescente de energia
                  será destinado como crédito de energia em favor da unidade consumidora de maior
                  consumo medido no ciclo de faturamento em questão.
                </Text>
              </>
            )}

            <Text
              style={{
                fontSize: 12,
                fontFamily: "Inter",
                textAlign: "center",
                paddingTop: 40,
              }}
            >
              __________________________, _____ de ____________________ de _______________
            </Text>

            <Text
              style={{
                fontSize: 12,
                fontFamily: "Inter",
                textAlign: "center",
                fontWeight: "semibold",
              }}
            >
              Local e data
            </Text>

            <Text
              style={{
                fontSize: 12,
                fontFamily: "Inter",
                textAlign: "center",
                paddingTop: 5,
                fontWeight: "semibold",
              }}
            >
              ___________________________________________________________________________________
            </Text>

            <Text
              style={{
                fontSize: 12,
                fontFamily: "Inter",
                textAlign: "center",
                fontWeight: "semibold",
              }}
            >
              Assinatura do cliente
            </Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default Pdf;
